import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

// 183 -- shared chrome for the anonymous (PublicLayout) and no-role
// (MinimalLayout) headers. Both previously crammed brand + 4 buttons into a
// single non-wrapping row that overflowed below ~400px. Here the secondary nav
// (Events / Shop / ...) folds into an overflow menu below `sm`, while the
// primary actions (auth CTAs / sign-out) stay visible. At `sm`+ the original
// single-row layout renders unchanged.

export interface PublicHeaderAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  variant?: "default" | "outline" | "ghost";
  dataAction?: string;
}

function ActionButton({ action, inMenu }: { action: PublicHeaderAction; inMenu?: boolean }) {
  const Icon = action.icon;
  return (
    <Button
      type="button"
      variant={action.variant ?? "ghost"}
      size="sm"
      className={`gap-2 ${inMenu ? "min-h-11 w-full justify-start" : ""}`}
      onClick={action.onClick}
      data-action={action.dataAction}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {action.label}
    </Button>
  );
}

export function PublicHeader({
  navItems,
  actions,
}: {
  /** Secondary nav -- inline at `sm`+, folded into the overflow menu below `sm`. */
  navItems: PublicHeaderAction[];
  /** Primary actions -- always visible (auth CTAs / sign-out). */
  actions: PublicHeaderAction[];
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header className="flex h-14 items-center justify-between border-b border-border px-4 md:px-6">
      <Link to="/" className="text-lg font-semibold hover:opacity-80" data-action="home">
        Dugads
      </Link>
      <div className="flex items-center gap-2">
        {/* Secondary nav: inline at sm+, hidden (folded into the menu) below sm */}
        <div className="hidden items-center gap-2 sm:flex">
          {navItems.map((a) => (
            <ActionButton key={a.label} action={a} />
          ))}
        </div>

        {/* Primary actions: always visible */}
        {actions.map((a) => (
          <ActionButton key={a.label} action={a} />
        ))}

        {/* Overflow menu: only below sm, only when there is secondary nav to fold */}
        {navItems.length > 0 && (
          <div className="relative sm:hidden" ref={menuRef}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-10 w-10 p-0"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
              data-action="nav-menu"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full z-50 mt-1 flex w-48 flex-col gap-1 rounded-md border border-border bg-background p-1 shadow-lg"
                role="menu"
                data-region="public-nav-menu"
              >
                {navItems.map((a) => (
                  <ActionButton key={a.label} action={a} inMenu />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
